"use strict";
/**
 * Pure markdown-text helpers: HTML-entity decoding, line-end
 * normalization, table / list / value-section builders. No DOM, no React.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.toMarkdownValue = exports.buildMarkdownListSection = exports.splitOrderedListValues = exports.toMarkdownValueOrList = exports.splitBulletListValues = exports.splitDashListValues = exports.splitCommaSeparatedValues = exports.buildMarkdownTable = exports.normalizeReportText = void 0;
var decodeHtmlEntityMap = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'",
};
/**
 * Normalize raw markdown-like report text: decode common HTML entities,
 * collapse Windows line endings, and trim surrounding whitespace.
 */
exports.normalizeReportText = function (value) {
    var source = value || '';
    return Object.keys(decodeHtmlEntityMap).reduce(function (result, key) {
        return result.split(key).join(decodeHtmlEntityMap[key]);
    }, source.replace(/\r\n/g, '\n').trim());
};
var toSingleLineText = function (value) {
    return exports.normalizeReportText(value)
        .replace(/\s*\n\s*/g, ' ')
        .trim();
};
var escapeMarkdownTableCell = function (value) {
    return toSingleLineText("" + (value || '-')).replace(/\|/g, '\\|');
};
/**
 * Build a Github-flavoured markdown table. Returns the empty string if
 * either dimension is empty so the caller can omit the section entirely.
 */
exports.buildMarkdownTable = function (headers, rows) {
    if (headers.length === 0 || rows.length === 0) {
        return '';
    }
    var headerRow = "| " + headers.join(' | ') + " |";
    var dividerRow = "| " + headers.map(function () { return '---'; }).join(' | ') + " |";
    var bodyRows = rows.map(function (row) { return "| " + row.map(function (cell) { return escapeMarkdownTableCell(cell); }).join(' | ') + " |"; });
    return [headerRow, dividerRow].concat(bodyRows).join('\n');
};
exports.splitCommaSeparatedValues = function (value) {
    return exports.normalizeReportText(value)
        .split(',')
        .map(function (item) { return item.trim(); })
        .filter(Boolean);
};
exports.splitDashListValues = function (value) {
    return exports.normalizeReportText(value)
        .split('\n')
        .map(function (line) { return line.replace(/^[-*]\s+/, '').trim(); })
        .filter(function (line) { return !!line && !/^[-*]\s*$/.test(line); });
};
// Matches "• ", "- " or "* " when used as a list marker. Used both to
// detect that a value is a bullet list and to split on subsequent items
// that may appear on the same line (e.g. "• foo • bar • baz").
var BULLET_MARKER = /[•\-*]/;
var LEADING_BULLET = /^[•\-*]\s+/;
var BULLET_SPLIT = /(?:\n|\s)(?=[•\-*]\s+)/;
/**
 * If `value` starts with a bullet marker ("• ", "- " or "* "), return
 * the list items with the marker stripped. Returns an empty array when
 * the value is not a bullet list so the caller can fall back to
 * rendering it as plain text.
 */
exports.splitBulletListValues = function (value) {
    var text = exports.normalizeReportText(value);
    if (!text || !LEADING_BULLET.test(text)) {
        return [];
    }
    return text
        .split(BULLET_SPLIT)
        .map(function (item) { return item.replace(LEADING_BULLET, '').trim(); })
        .filter(Boolean);
};
/**
 * Render a value either as a bullet list (when it begins with "• ",
 * "- " or "* ") or as a plain markdown value section. Centralises the
 * "keep raw text unless the source clearly meant a list" rule.
 */
exports.toMarkdownValueOrList = function (label, value) {
    if (!value && value !== 0) {
        return '';
    }
    var asString = "" + value;
    if (BULLET_MARKER.test(asString.trimStart().charAt(0))) {
        var items = exports.splitBulletListValues(asString);
        if (items.length > 0) {
            return exports.buildMarkdownListSection(label, items, false);
        }
    }
    return exports.toMarkdownValue(label, asString);
};
exports.splitOrderedListValues = function (value) {
    var text = exports.normalizeReportText(value);
    if (!text)
        return [];
    // Split on either newline-prefixed numbers ("1. …\n2. …") or inline
    // numbered markers in a single-line string ("1. … 2. … 3. …").
    var parts = text.split(/(?:\n|\s)(?=\d+[.)]\s+)/);
    return parts.map(function (item) { return item.replace(/^\d+[.)]\s+/, '').trim(); }).filter(Boolean);
};
/**
 * Render a labelled markdown list. Returns the empty string when there
 * are no items, matching {@link buildMarkdownTable}.
 */
exports.buildMarkdownListSection = function (label, items, isOrdered) {
    if (isOrdered === void 0) { isOrdered = false; }
    if (items.length === 0) {
        return '';
    }
    var lines = items.map(function (item, index) { return (isOrdered ? index + 1 + ". " + item : "- " + item); });
    return ["## " + label].concat([''], lines).join('\n');
};
/**
 * Render a single labelled value as `## Label\n\nvalue`. Empty values
 * collapse to the empty string so the join below them stays clean.
 */
exports.toMarkdownValue = function (label, value) {
    if (!value && value !== 0) {
        return '';
    }
    return "## " + label + "\n\n" + exports.normalizeReportText("" + value);
};
